import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  Layers,
  Recycle,
  CircleCheck,
  Tag,
  Boxes,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison directe et rapide + 3 engagements ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison directe
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison directe et rapide de vos cartons à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Simplifiez vos préparatifs et évitez la logistique fastidieuse
            du transport d&apos;emballages. Nous livrons tous vos cartons et
            fournitures directement à votre adresse à {city.name} et dans
            toute l&apos;Île-de-France. Grâce à notre propre flotte de
            véhicules, sans aucun intermédiaire, nous vous garantissons un
            service de proximité rapide, ponctualité et soin maximal de vos
            produits. Recevez vos équipements au pied de votre porte et
            commencez à emballer l&apos;esprit tranquille.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Aucun intermédiaire",
              body: "Notre propre flotte gère la livraison de bout en bout, sans sous-traitance.",
              Icon: Truck,
            },
            {
              title: "Rapidité et ponctualité",
              body: "Créneaux respectés à la minute pour ne jamais bloquer votre planning de préparation.",
              Icon: ShieldCheck,
            },
            {
              title: "Soin maximal du matériel",
              body: "Transport en véhicules fermés ; vos cartons arrivent sains, secs et prêts à l'emploi.",
              Icon: Package,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Packs complets + zoom Kit T3 ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Solution clé en main
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos packs complets de cartons : la solution clé en main
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour vous faire gagner du temps, nous avons conçu des kits et
            packs de cartons complets adaptés à la taille de votre logement
            à {city.name}. Que vous déménagiez un studio ou une grande
            maison familiale, nos packs regroupent l&apos;essentiel des
            fournitures nécessaires : des cartons de tailles différentes
            (standards et livres), des rouleaux d&apos;adhésif et du film
            bulle de protection. C&apos;est la formule idéale pour être sûr
            de ne manquer de rien tout en bénéficiant d&apos;un tarif
            avantageux.
          </p>
        </div>

        {/* === Zoom Kit T3 === */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 bg-[#0f1d2b] text-white rounded-3xl p-6 md:p-8 relative overflow-hidden">
            <div className="absolute -top-12 -right-12 h-44 w-44 rounded-full bg-[#237cb5]/20 blur-2xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF7A00] text-white text-[11px] uppercase tracking-[0.18em] font-bold mb-4">
                <Tag className="h-3.5 w-3.5" strokeWidth={2.6} />
                Exemple — Kit T3
              </div>
              <h3 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px]">
                Le kit T3 pour appartement 3 pièces
              </h3>
              <p className="mt-2 text-white/70 text-[14px]">
                Idéal pour une surface de 55 à 75 m² · livraison Île-de-France
              </p>

              <ul className="mt-6 space-y-3">
                {[
                  {
                    qty: "60",
                    name: "cartons standards",
                    tag: "Le gros volume indispensable pour emballer le quotidien de toute la famille.",
                  },
                  {
                    qty: "30",
                    name: "cartons livres",
                    tag: "Costauds et parfaits pour vos collections de BD, romans, archives ou objets lourds.",
                  },
                  {
                    qty: "1",
                    name: "grand rouleau de papier bulle (0,5 m × 100 m)",
                    tag: "100 mètres ! La sécurité ultime pour toute votre vaisselle et vos bibelots.",
                  },
                  {
                    qty: "6",
                    name: "rouleaux d'adhésif pro",
                    tag: "Le stock parfait pour scotcher à la chaîne comme un vrai pro de la manutention.",
                  },
                ].map(({ qty, name, tag }) => (
                  <li key={name} className="flex items-start gap-3">
                    <span className="flex items-center justify-center min-w-[2.5rem] h-9 px-2 rounded-full bg-white text-[#0f1d2b] font-display font-extrabold text-[14px] shrink-0">
                      ×{qty}
                    </span>
                    <div>
                      <div className="font-display font-extrabold text-[15px] text-white">
                        {name}
                      </div>
                      <p className="mt-0.5 text-[13.5px] text-white/70 leading-relaxed">
                        {tag}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-7">
                <Link
                  to="/produit/pack-cartons-demenagement-t3"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#FF7A00] hover:bg-[#E66E00] text-white font-bold text-[14px] transition-colors"
                  data-testid="city-bagneux-cta-pack-t3"
                >
                  Voir le Kit T3
                  <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
                </Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            {[
              { name: "Kit T1", body: "Studio · 1 pièce", to: "/produit/pack-cartons-demenagement-t1" },
              { name: "Kit T2", body: "Appartement 2 pièces", to: "/produit/pack-cartons-demenagement-t2" },
              { name: "Kit T3", body: "Appartement 3 pièces", to: "/produit/pack-cartons-demenagement-t3" },
              { name: "Kit Maison", body: "Maison · 4+ pièces", to: "/produit/pack-cartons-demenagement-maison" },
            ].map(({ name, body, to }) => (
              <Link
                key={name}
                to={to}
                className="bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] hover:border-[#237cb5] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(35,124,181,0.35)] transition-all duration-300 p-4 text-center"
              >
                <div className="mx-auto flex items-center justify-center h-9 w-9 rounded-full bg-[#237cb5] text-white">
                  <Boxes className="h-4 w-4" strokeWidth={2.6} />
                </div>
                <div className="mt-2.5 font-display font-extrabold text-[14px] text-[#0f1d2b]">
                  {name}
                </div>
                <div className="mt-1 text-[11.5px] text-[#5b6b7a] leading-tight">
                  {body}
                </div>
              </Link>
            ))}
          </div>
        </div>

        <p className="mt-8 text-[14px] text-[#5b6b7a]">
          Découvrez tous nos kits déménagement et choisissez celui qui
          convient le mieux à votre projet.
        </p>
        <div className="mt-4">
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-bagneux-cta-packs"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les kits
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Pourquoi choisir (3 engagements) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Nos engagements
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi choisir nos fournitures pour votre déménagement&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Faire confiance à notre boutique, c&apos;est l&apos;assurance
            d&apos;un déménagement réussi grâce à des engagements clairs.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Qualité et robustesse professionnelle",
              body: "Nos cartons sont ultra-résistants et spécialement fabriqués pour supporter le transport et le stockage sans s'affaisser.",
              Icon: ShieldCheck,
              accent: "#237cb5",
            },
            {
              title: "Engagement écoresponsable",
              body: "Soucieux de l'environnement, nous proposons des cartons 100 % recyclables et biodégradables pour limiter l'empreinte écologique de votre projet.",
              Icon: Recycle,
              accent: "#16A34A",
            },
            {
              title: "Service de livraison intégré",
              body: "Nous maîtrisons notre logistique de A à Z en Île-de-France, vous garantissant un interlocuteur unique et une flexibilité totale pour la réception de votre matériel.",
              Icon: Truck,
              accent: "#237cb5",
            },
          ].map(({ title, body, Icon, accent }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span
                  className="flex items-center justify-center h-10 w-10 rounded-full text-white"
                  style={{ backgroundColor: accent }}
                >
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-bagneux-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
